import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Download, ExternalLink } from "lucide-react";

export default function ApparelPreview() {
  const [manifest, setManifest] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/apparel/preview-manifest");
        setManifest(data);
      } catch (e) {
        setError("Preview manifest could not be loaded.");
      }
    })();
  }, []);

  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const zipUrl = `${backend}/api/apparel/preview.zip`;

  return (
    <div data-testid="apparel-preview-page" className="max-w-[1440px] mx-auto px-6 lg:px-10 pt-32 pb-24">
      {/* Header */}
      <div className="mb-16 reveal">
        <p className="eyebrow mb-4">Luxury Apparel &amp; Accessory Line</p>
        <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.95] mb-6 max-w-4xl">
          Eleven references. <em style={{ color: "var(--brass)" }}>One mini-mark.</em>
        </h1>
        <p className="text-lg max-w-2xl leading-relaxed" style={{ color: "var(--ink-muted)" }}>
          Preview renderings of the WEALTH FIRST INC. apparel &amp; accessory line — the WFI shield
          reproduced faithfully across polos, tees, socks, hoodies, calf-leather sneakers, caps, and a small
          collection of gentleman's objects. For reference, decks, and manufacturer discussions.
        </p>

        <div className="mt-10 flex flex-wrap items-center gap-4">
          <a
            href={zipUrl}
            data-testid="apparel-preview-download-zip"
            className="inline-flex items-center gap-3 px-8 py-4 text-xs uppercase tracking-[0.18em]"
            style={{ background: "var(--brass)", color: "var(--bg)" }}
          >
            <Download size={14} />
            Download Preview Bundle (.zip)
          </a>
          <a
            href="/brand-kit/wfi-manufacturing-specs.html"
            target="_blank"
            rel="noreferrer"
            data-testid="apparel-preview-specs-link"
            className="inline-flex items-center gap-3 px-8 py-4 text-xs uppercase tracking-[0.18em]"
            style={{ border: "1px solid var(--hairline)", color: "var(--ink)" }}
          >
            <ExternalLink size={14} />
            Open Manufacturing Specs
          </a>
          <Link
            to="/brand-kit-access"
            data-testid="apparel-preview-gated-link"
            className="brass-link text-xs uppercase tracking-[0.18em]"
          >
            Manufacturers → Full Brand Kit ↗
          </Link>
        </div>
      </div>

      <hr className="hairline mb-16" />

      {/* Master shield */}
      {manifest?.master_vector && (
        <div className="mb-20">
          <p className="eyebrow mb-4">Official WFI Shield</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div
              className="flex items-center justify-center p-8"
              style={{ background: "#000", border: "1px solid var(--hairline)", minHeight: 260 }}
            >
              <img
                src="/brand-kit/logo/wfi-shield-reference.png"
                alt="WFI shield reference"
                style={{ maxHeight: 220, width: "auto" }}
                data-testid="master-vector-preview"
              />
            </div>
            <div className="md:col-span-2 flex flex-col justify-center">
              <h3 className="font-display text-3xl mb-4">One shield. One story.</h3>
              <p className="text-sm leading-relaxed" style={{ color: "var(--ink-muted)" }}>
                Gold border, black field, white crown, three stars, and the WFI monogram — embroidered,
                debossed, or engraved on every product below. The mark is intentionally small and precise:
                a signal to those who know, never a shout to those who don't.
              </p>
              <a
                href="/brand-kit/logo/wfi-shield-reference.png"
                download
                data-testid="master-vector-download"
                className="brass-link text-xs uppercase tracking-[0.18em] mt-6 inline-flex items-center gap-2"
              >
                <Download size={12} /> Download Shield Reference
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Mockup grid */}
      <div className="mb-6">
        <p className="eyebrow mb-4">The Eleven</p>
        <h2 className="font-display text-3xl md:text-4xl mb-2">Preview renderings</h2>
        <p className="text-sm mb-10" style={{ color: "var(--ink-faint)" }}>
          AI-rendered from your reference examples. Click any card to open the full-resolution image.
        </p>
      </div>

      {error && (
        <p className="text-sm" style={{ color: "#E08585" }} data-testid="apparel-preview-error">{error}</p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {(manifest?.mockups || []).map((m, idx) => (
          <a
            key={m.filename}
            href={m.url}
            target="_blank"
            rel="noreferrer"
            data-testid={`mockup-card-${idx + 1}`}
            className="group block overflow-hidden"
            style={{ border: "1px solid var(--hairline)", background: "var(--surface)" }}
          >
            <div
              className="relative overflow-hidden"
              style={{ aspectRatio: "1 / 1", background: "#0a0a0a" }}
            >
              <img
                src={m.url}
                alt={m.title}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700"
                style={{ transform: "scale(1)" }}
                onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.04)")}
                onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
              />
            </div>
            <div className="p-5 flex items-start justify-between gap-4">
              <div>
                <p className="text-[0.625rem] uppercase tracking-[0.22em] mb-2" style={{ color: "var(--ink-faint)" }}>
                  Ref &middot; {String(idx + 1).padStart(2, "0")}
                </p>
                <h3 className="text-sm leading-snug" style={{ color: "var(--ink)" }}>
                  {m.title}
                </h3>
              </div>
              <span
                className="shrink-0 inline-flex items-center justify-center w-9 h-9 group-hover:opacity-100 opacity-60 transition-opacity"
                style={{ border: "1px solid var(--brass)", color: "var(--brass)" }}
                aria-hidden="true"
              >
                <Download size={14} />
              </span>
            </div>
          </a>
        ))}
      </div>

      {/* Legal footer */}
      <div className="mt-24 text-center">
        <hr className="hairline mb-8" />
        <p className="text-[0.625rem] uppercase tracking-[0.22em] mb-2" style={{ color: "var(--ink-faint)" }}>
          Reference only
        </p>
        <p className="text-xs max-w-2xl mx-auto leading-relaxed" style={{ color: "var(--ink-muted)" }}>
          These renderings are pre-production concept references. Final sample garments will be produced by
          approved manufacturers under the WealthFirst Brand License. Not for resale.
        </p>
      </div>
    </div>
  );
}
